<template v-cloak>

	<!-- Header -->
	<Head :title="formData.title"/>

	<!-- Form Landing Page Design -->
	<div v-if="! isProcessing">

		<!-- If not set content on landing page -->
		<template v-if="! formData.form_header_page_template">

			<!-- Temporary hide, later need to use for Facebook OG -->
			<div class="d-none">
				<img v-if="formData.thumbnail" class="card-img-top mb-5" :src="formData.thumbnail">

				<h4 class="mb-3">
					{{ formData.title }}
				</h4>

				<p class="mb-3 text-14" v-html="formData.descriptions"></p>
			</div>
		</template>

		<!-- If set content on landing page -->
		<template v-else>
			<!-- Sandboxed iframe isolates merchant HTML/CSS/JS from the form -->
			<iframe
				v-if="formData.form_header_page_template_editor === 'html'"
				:srcdoc="htmlHeaderSrcdoc"
				class="w-100 d-block border-0"
				scrolling="no"
				@load="resizeHtmlFrame"
			></iframe>
			<div v-else class="container col-5 pt-4 mobile-fix-container">
				<div id="custom-template-wrapper" class="bg-white border landing-page-form-padding rounded mobile-fix-rounded" v-html="formData.form_header_page_template"></div>
			</div>
		</template>
	</div>

	<!-- Donation Form -->
	<main>
		<div class="container col-5 mobile-fix-container">
			<div id="landing-page-form" class="mt-4 mb-4 mobile-fix-rounded">

				<!-- Alert -->
				<div class="alert alert-danger" v-if="Object.keys($attrs.errors).length && ! isProcessing">
					<ul class="mb-0">
						<template v-for="err_key in $attrs.errors">
							<li>{{ err_key }}</li>
						</template>
					</ul>
				</div>

				<!-- Step 1: Donation -->
				<h4 class="mb-3" v-show="! isProcessing">
					<span class="badge badge-danger step">
						{{ $t('donation-form.step') }} 1
					</span>
					{{ $t('donation-form.donation_details') }}
				</h4>

				<!-- Package -->
        <PackageSection v-show="! isProcessing && formData.mandate_application_type !== '03'" :form-data="formData" :donations="donations" :summary="summary" :campaigns="campaigns" @plus="plus" @minus="minus" :total="total" :errors="formErrors" :payment-gateway-fee="paymentGatewayFee" />

        <PackageSectionDisplayOnly v-show="! isProcessing && formData.mandate_application_type === '03'" :form-data="formData" :donations="donations" :summary="summary" :campaigns="campaigns" @plus="plus" @minus="minus" :total="total" />

				<!-- Step 2: Donor -->
				<!-- Step 3: Payment -->
				<hr v-show="! isProcessing">

				<h4 class="mb-3" v-show="! isProcessing">
					<span class="badge badge-danger step">
						{{ $t('donation-form.step') }} 2
					</span>
					{{ $t('donation-form.donor_details') }}
				</h4>

				<payment-section
		            :form-data="formData"
		            :total="total"
		            :summary="summary"
		            :city-opts="cityOpts"
		            :state-opts="stateOpts"
		            :country-opts="countryOpts"
		            :bank-accounts="bankAccounts"
		            :payment-gateways="paymentGateways"
		            :tenant="tenant"
		            :demo_mode="formData.demo_mode"
		            :fpx-banks-list="fpxBanksList"
		            :fpx-banks-logo="fpxBanksLogo"
		            @processing="isProcessing=true"
		            @done-processing="doneProcessing"
                @payment-method-selected="(fee) => paymentGatewayFee = fee"
		        />
			</div>

			<!-- FOMO Options -->
			<template v-if="props.formData.scroll_to_form_btn_enabled">
				<img v-if="props.formData.scroll_to_form_btn_image_enabled" :src="props.formData.scroll_to_form_btn_image_url" id="scroll-to-form-btn">
				<a v-else class="btn btn-primary btn-lg text-white" id="scroll-to-form-btn">
					{{ props.formData.scroll_to_form_btn_text }}
				</a>
			</template>

			<!-- Footer -->
			<footer-section :tenant="tenant"/>
		</div>
	</main>
</template>

<!-- Scripts -->
<script setup>
import {computed, onBeforeMount, ref, onMounted, inject, nextTick} from "vue";
import {Head} from "@inertiajs/vue3";
import PackageSection from "@/Pages/Donations/ClassicForm/PackageSection";
import PaymentSection from "@/Pages/Donations/ClassicForm/PaymentSection";
import FooterSection from "@/Components/FooterSection.vue";
import useHelpers from "@/Composables/UseHelpers";
import useTrackingCode from "@/Composables/UseTrackingCode";
import useLandingPageFrame from "@/Composables/UseLandingPageFrame";
import PackageSectionDisplayOnly from "@/Pages/Donations/ClassicForm/PackageSectionDisplayOnly";

const {  sendFacebookPixelAddToCartEvent, sendGoogleAdsAddToCartEvent} = useTrackingCode();

const { makeSlug, $t, onlyNumber } = useHelpers();

const props = defineProps(['tenant', 'formData', 'bankAccounts', 'campaigns', 'cityOpts', 'stateOpts', 'countryOpts', 'paymentGateways', 'latestTransactions', 'fpxBanksList', 'fpxBanksLogo']);

Lang.setLocale(props.formData.language ?? 'en')

const donations = ref([]);
const summary = ref(Object.create({}));
const isProcessing = ref(false);
const formHasErrors = ref(false);
const formErrors = ref({});
const paymentGatewayFee = ref('');
const toastr = inject('toastr');

const { htmlHeaderSrcdoc, resizeHtmlFrame } = useLandingPageFrame(props.formData);

const total = computed(() => {
	return donations.value.map(donation => donation.amount * donation.count).reduce((prev, next) => prev + next, 0.00);
});

onBeforeMount(() => {
	props.campaigns.forEach((campaign) => {
		campaign.packages.forEach((cpackage) => {
			donations.value.push(cpackage);
		});
	});

	summary.value = donations.value.reduce(function (r, a) {
		r[a.campaign] = r[a.campaign] || [];
		r[a.campaign].push(a);

		return r;
	}, Object.create(null));
});

// FOMO Options
onMounted(() => {
	if (props.formData.social_proof_enabled) {
		props.latestTransactions.forEach((donation, idx) => {
			setTimeout(function() {
				toastr.success(`${donation.name} telah menyumbang sebanyak ${donation.amount} pada ${donation.date}...`, {
					position: 'top-right',
					duration: 4000,
				});
			}, 2500 * ((idx+1) * 3));
		});
	}

	// Scroll button
	let scrollButtons = document.querySelectorAll('a[href="#landing-page-form"], #scroll-to-form-btn');

	for (let i = 0; i < scrollButtons.length; i++) {
		scrollButtons.item(i).addEventListener('click', function handleClick(event) {
			event.preventDefault();
			document.querySelector('#landing-page-form').scrollIntoView({
				behavior: 'smooth'
			});
		});
	}

	$(window).scroll(function() {
		var formTopOffset = getCoords(document.getElementById('landing-page-form')).top;

		if ($(this).scrollTop() > (formTopOffset - 80)) {
			$('#scroll-to-form-btn').hide();
		} else {
			$('#scroll-to-form-btn').show();
		}
	});

});

// Cross browser version
function getCoords(elem) {
	let box = elem.getBoundingClientRect(),
	body = document.body,
	docEl = document.documentElement,
	scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop,
	scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft,
	clientTop = docEl.clientTop || body.clientTop || 0,
	clientLeft = docEl.clientLeft || body.clientLeft || 0,
	top  = box.top +  scrollTop - clientTop,
	left = box.left + scrollLeft - clientLeft;

	return { top: Math.round(top), left: Math.round(left) };
}

function doneProcessing(result) {
	isProcessing.value  = false;
	formHasErrors.value = result.hasErrors;
    formErrors.value = result.errors;

	if (formHasErrors) {
		nextTick(() =>
		document.getElementById('landing-page-form').scrollIntoView({
				behavior: 'smooth'
			})
		);
	}
}

function plus(cpackage) {
  if (cpackage.count > 99) return;

	cpackage.count++;

	// Meta (Facebook) Pixel (Standard Events)
	if (props.formData.facebook_tracking.facebook_pixel_id) {
		sendFacebookPixelAddToCartEvent(cpackage, props.formData, props.formData.module);
	}

	// Google Ads Tracking
	if (props.formData.google_ads_tracking.google_ads_id && props.formData.google_ads_tracking.add_to_cart_conversion_label) {
		sendGoogleAdsAddToCartEvent(cpackage, props.formData, props.formData.module);
	}
}

function minus(cpackage) {
	if (cpackage.count === 0) return;
	cpackage.count--;
}
</script>

<!-- Styles -->
<style>
[v-cloak] {
	display: none;
}
</style>
